import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layout
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Agents from './pages/Agents';
import AgentEditor from './pages/AgentEditor';
import VoiceTest from './pages/VoiceTest';
import Calls from './pages/Calls';
import Monitor from './pages/Monitor';
import Credits from './pages/Credits';
import Users from './pages/Users';
import Gateways from './pages/Gateways';

// IVR Pages
import IVRConfig from './pages/IVRConfig';
import AudioLibrary from './pages/AudioLibrary';
import Flows from './pages/Flows';
import Scripts from './pages/Scripts';
import ApiKeys from './pages/ApiKeys';
import Scheduler from './pages/Scheduler';
import WhatsAppInbox from './pages/WhatsAppInbox';

// Analytics
import Analytics from './pages/Analytics';

// Placeholder pages (for features to be implemented)
import {
  SegmentsList,
  TemplatesList,
  TemplateBuilder,
  LanguageSettings
} from './pages/placeholders';

// Protected Route Component
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1e293b',
              color: '#f8fafc',
              borderRadius: '8px',
            },
            success: {
              iconTheme: { primary: '#22c55e', secondary: '#f8fafc' },
            },
            error: {
              iconTheme: { primary: '#ef4444', secondary: '#f8fafc' },
            },
          }}
        />
        
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected */}
          <Route path="/" element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
            {/* Main */}
            <Route index element={<Dashboard />} />
            <Route path="monitor" element={<Monitor />} />
            
            {/* Agents */}
            <Route path="agents" element={<Agents />} />
            <Route path="agents/new" element={<AgentEditor />} />
            <Route path="agents/:id" element={<AgentEditor />} />
            
            {/* IVR Configuration */}
            <Route path="agents/:agentId/ivr" element={<IVRConfig />} />
            <Route path="agents/:agentId/audio" element={<AudioLibrary />} />
            <Route path="agents/:agentId/flows" element={<Flows />} />
            <Route path="agents/:agentId/scripts" element={<Scripts />} />
            <Route path="agents/:agentId/ivr/segments" element={<SegmentsList />} />
            <Route path="agents/:agentId/ivr/templates" element={<TemplatesList />} />
            <Route path="agents/:agentId/ivr/templates/new" element={<TemplateBuilder />} />
            <Route path="agents/:agentId/ivr/templates/:templateId" element={<TemplateBuilder />} />
            <Route path="agents/:agentId/ivr/languages" element={<LanguageSettings />} />
            
            {/* Testing */}
            <Route path="test" element={<VoiceTest />} />
            
            {/* Calls */}
            <Route path="calls" element={<Calls />} />
            <Route path="whatsapp" element={<WhatsAppInbox />} />
            
            {/* Analytics */}
            <Route path="analytics" element={<Analytics />} />
            <Route path="scheduler" element={<Scheduler />} />
            {/* Administration */}
            <Route path="credits" element={<Credits />} />
            <Route path="users" element={<Users />} />
			<Route path="api-keys" element={<ApiKeys />} />
			<Route path="gateways" element={<Gateways />} />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
